import React, { useEffect, useState } from "react";
import { ShoppingBag, Leaf, Droplet, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CtaButton } from "@/components/CtaButton";
import { SITE } from "@/constants/testIds";

const CATEGORIES = [
    {
        icon: <Droplet className="h-5 w-5 text-moss" strokeWidth={1.5} />,
        title: "Oils & Lepa",
        description: "Hand-crafted dosha-specific massage oils & herbal pastes.",
    },
    {
        icon: <Leaf className="h-5 w-5 text-moss" strokeWidth={1.5} />,
        title: "Herbs & Formulas",
        description: "Classical herbal formulas, churnas, and rasayanas.",
    },
    {
        icon: <Sparkles className="h-5 w-5 text-moss" strokeWidth={1.5} />,
        title: "Teas & Tonics",
        description: "CCF tea, Triphala-based blends, and daily wellness tonics.",
    },
];

export default function ShopPage() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.siteInfo().then(setInfo).catch(() => {});
    }, []);

    return (
        <div>
            <PageHeader
                eyebrow="ONLINE SHOP"
                title="Practice continues at home"
                subtitle="Carefully curated Ayurvedic oils, herbs, teas, and tools — the same ones Riley uses in clinic."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Shop" }]}
            />

            <section className="py-10">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div
                        data-testid={SITE.shopTeaserBanner}
                        className="relative overflow-hidden rounded-3xl bg-moss text-cream p-8 sm:p-12"
                    >
                        <div className="absolute inset-0 opacity-10 pointer-events-none">
                            <img
                                src="https://images.unsplash.com/photo-1612207259452-1bce85be5dee?w=1600&auto=format&fit=crop"
                                alt="herbs"
                                className="h-full w-full object-cover"
                            />
                        </div>
                        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                            <div>
                                <p className="font-accent text-xs text-clay">EXPLORE</p>
                                <h2 className="mt-2 font-display text-3xl sm:text-4xl leading-tight">
                                    Visit the Choice Ayurveda online shop
                                </h2>
                                <p className="mt-3 max-w-prose text-cream/85">
                                    Browse practitioner-grade Ayurvedic products and have them shipped directly to your door.
                                </p>
                                <div className="mt-5 inline-flex items-center gap-3">
                                    <CtaButton
                                        href={info?.shop_url || "https://choiceayurveda.com/onlineshop"}
                                        variant="accent"
                                        testid={SITE.shopTeaserCta}
                                    >
                                        Go to shop
                                    </CtaButton>
                                </div>
                            </div>
                            <div className="justify-self-center md:justify-self-end">
                                <div className="h-32 w-32 rounded-full bg-cream/15 flex items-center justify-center">
                                    <ShoppingBag className="h-12 w-12" strokeWidth={1.5} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-10 sm:py-16">
                <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="font-accent text-xs text-terracotta">WHAT YOU’LL FIND</p>
                        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-moss leading-tight">
                            Considered, classical, and clean
                        </h2>
                        <p className="mt-3 text-foreground/75 leading-relaxed">
                            We carry only products that meet our standards for purity, tradition, and effectiveness.
                        </p>
                    </div>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
                        {CATEGORIES.map((c) => (
                            <div
                                key={c.title}
                                className="rounded-2xl border border-border/80 bg-white/85 p-6"
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sand">
                                    {c.icon}
                                </div>
                                <h3 className="mt-3 font-display text-lg text-moss">{c.title}</h3>
                                <p className="mt-1 text-sm text-foreground/75 leading-relaxed">
                                    {c.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
